import React, { useState, useEffect, useRef } from "react";
import {
  getProjectDetails,
  updateProjectDetail,
  uploadProductImage,
} from "../StepperService";
import {
  openErrorNotification,
  openSuccessNotification,
} from "../../../../../helpers/methods";
import { getProjectFilterMeta } from "../../../../MetaData/components/MetaList/components/ProjectFilterMeta/Service";
import { getLocationById } from "../../../../MetaData/components/MetaList/components/ProjectLocationList/projectLocationService";
import { YhButton, YhInput, YhSelect } from "../../../../../ui";

interface CustomProps {
  nextStep?: () => void | undefined;
  backStep?: () => void | undefined;
  userId: String;
  idd: string;
  data?: any;
  fromSettingOrLogout?: any;
}

type FloorPlanType = "2D" | "3D";

const INR_TO_AED_RATE = 0.044;

const emptyUnit = () => ({
  propertyType: "",
  bedroom: "",
  bathroom: "",
  carpetArea: "",
  price: "",
  floorPlan: [] as Array<{ type: FloorPlanType; images: string[] }>,
});

const BATHROOM_OPTIONS = ["1", "2", "3", "4", "5", "6", "7", "8"].map((n) => ({
  label: n,
  value: n,
}));

const FieldLabel = ({ children }: { children: React.ReactNode }) => (
  <div className="mb-2 flex items-center gap-1.5">
    <span className="text-sm font-semibold text-[#111827]">{children}</span>
    <span
      className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-[#D1D5DB] text-[10px] text-[#9CA3AF]"
      title="More info"
    >
      i
    </span>
  </div>
);

const normalizeImageUrls = (uploaded: any): string[] => {
  if (!uploaded) return [];
  const list = Array.isArray(uploaded) ? uploaded : [uploaded];
  return list
    .map((item) => {
      if (typeof item === "string") return item;
      return (
        item?.imageUrl ||
        item?.url ||
        item?.path ||
        item?.Location ||
        ""
      );
    })
    .filter(Boolean);
};

const PriceInfo = (props: CustomProps) => {
  const [offialState, setOffialState] = useState<any>({
    priceList: [emptyUnit()],
    project_name: "",
  });
  const [state, setState] = useState<any>({
    bedrooms: [],
    propertyType: [],
    currency: "INR",
  });
  const [floorPlanTab, setFloorPlanTab] = useState<Record<number, FloorPlanType>>(
    { 0: "2D" }
  );
  const [uploadingIndex, setUploadingIndex] = useState<number | null>(null);
  const fileInputRefs = useRef<Record<number, HTMLInputElement | null>>({});

  const setStateData = (newData: any) =>
    setState((prev: any) => ({ ...prev, ...newData }));

  const setOffialDetailState = (newData: any) =>
    setOffialState((prev: any) => ({ ...prev, ...newData }));

  const handleAddPriceInfo = () => {
    const nextIndex = (offialState.priceList || []).length;
    setOffialDetailState({
      priceList: [...(offialState.priceList || []), emptyUnit()],
    });
    setFloorPlanTab((prev) => ({ ...prev, [nextIndex]: "2D" }));
  };

  const handleChange = (index: number, field: string, value: any) => {
    const updated = [...(offialState.priceList || [])];
    updated[index] = { ...updated[index], [field]: value };
    setOffialDetailState({ priceList: updated });
  };

  const getActiveTab = (index: number): FloorPlanType =>
    floorPlanTab[index] || "2D";

  const handleFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;

    const data = new FormData();
    files.forEach((file) => data.append("files", file));

    try {
      setUploadingIndex(index);
      const uploaded = await uploadProductImage(
        data,
        offialState?.project_name || "project"
      );
      const newImages = normalizeImageUrls(uploaded);
      if (!newImages.length) {
        openErrorNotification("Upload succeeded but no image URL returned");
        return;
      }

      const type = getActiveTab(index);
      setOffialDetailState({
        priceList: (offialState?.priceList || []).map((item: any, idx: number) => {
          if (idx !== index) return item;
          const updatedFloorPlan = [...(item.floorPlan || [])];
          const existingTypeIndex = updatedFloorPlan.findIndex(
            (fp: any) => fp.type === type
          );
          if (existingTypeIndex !== -1) {
            updatedFloorPlan[existingTypeIndex] = {
              ...updatedFloorPlan[existingTypeIndex],
              images: [
                ...(updatedFloorPlan[existingTypeIndex].images || []),
                ...newImages,
              ],
            };
          } else {
            updatedFloorPlan.push({ type, images: newImages });
          }
          return { ...item, floorPlan: updatedFloorPlan };
        }),
      });
    } catch (err: any) {
      openErrorNotification(err?.message || "File upload failed");
    } finally {
      setUploadingIndex(null);
    }
  };

  const removeImage = (
    itemIndex: number,
    imageIndex: number,
    type: FloorPlanType
  ) => {
    const updatedPriceList = (offialState?.priceList || []).map(
      (item: any, idx: number) => {
        if (idx !== itemIndex) return item;
        const updatedFloorPlan = [...(item.floorPlan || [])];
        const typeIndex = updatedFloorPlan.findIndex((fp: any) => fp.type === type);
        if (typeIndex === -1) return item;
        const updatedImages = [...(updatedFloorPlan[typeIndex].images || [])];
        updatedImages.splice(imageIndex, 1);
        if (updatedImages.length === 0) {
          updatedFloorPlan.splice(typeIndex, 1);
        } else {
          updatedFloorPlan[typeIndex] = {
            ...updatedFloorPlan[typeIndex],
            images: updatedImages,
          };
        }
        return { ...item, floorPlan: updatedFloorPlan };
      }
    );
    setOffialDetailState({ priceList: updatedPriceList });
  };

  useEffect(() => {
    projectDetails();
  }, [props?.idd]);

  useEffect(() => {
    init();
  }, []);

  const projectDetails = async () => {
    if (props?.data?.priceList?.length) {
      setOffialDetailState({
        priceList: props.data.priceList,
        project_name: props.data.project_name || "",
        propertyPurpose: props.data.propertyPurpose || "",
      });
    } else if (props?.data?.propertyPurpose) {
      setOffialDetailState((prev: any) => ({
        ...prev,
        propertyPurpose: props.data.propertyPurpose,
        project_name: props.data.project_name || prev.project_name,
      }));
    }
    if (!props?.idd) return;
    try {
      const response = await getProjectDetails(props.idd);
      const data = response?.data;
      if ((data?.priceList || [])?.length) {
        setOffialDetailState({
          ...data,
          priceList: data.priceList,
          project_name: data.project_name || "",
        });
        const tabs: Record<number, FloorPlanType> = {};
        data.priceList.forEach((_: any, i: number) => {
          tabs[i] = "2D";
        });
        setFloorPlanTab(tabs);
      } else if (data) {
        setOffialDetailState((prev: any) => ({
          ...prev,
          project_name: data.project_name || prev.project_name,
          propertyPurpose: data.propertyPurpose || prev.propertyPurpose,
        }));
      }

      if (data?.countryId) {
        const countryData = await getLocationById(data.countryId);
        if (countryData?.data?.currency) {
          setStateData({ currency: countryData.data.currency });
        }
      }
    } catch (error: any) {
      openErrorNotification(
        error?.message || "Failed to load unit config details"
      );
    }
  };

  const init = async () => {
    try {
      const filterActive = (list: any[] = []) =>
        list.filter(
          (i) => i.status === true || i.status === 1 || i.status === "1"
        );
      const projectFilterData = await getProjectFilterMeta();
      const response = projectFilterData?.data?.[0] || {};
      setStateData({
        propertyType: filterActive(response.propertyType).map((i: any) => ({
          id: i._id,
          value: i.name,
        })),
        bedrooms: filterActive(response.bedrooms).map((i: any) => ({
          id: i._id,
          value: i.name,
          propertyPurpose: i.propertyPurpose || "",
        })),
      });
    } catch (error: any) {
      openErrorNotification(error?.message || "Failed to load unit options");
    }
  };

  const submitForm = async () => {
    try {
      if (!props?.idd) {
        openErrorNotification("Please save project information first");
        return;
      }
      const body = {
        _id: props.idd,
        priceList: (offialState.priceList || []).map((item: any) => ({
          ...item,
          bathroom:
            item.bathroom === "" || item.bathroom === null || item.bathroom === undefined
              ? undefined
              : Number(item.bathroom),
          floorPlan: (item.floorPlan || []).filter(
            (plan: any) =>
              Array.isArray(plan?.images) &&
              plan.images.some((src: unknown) => String(src || "").trim())
          ),
        })),
      };
      const response = await updateProjectDetail(body);
      if (response?.success) {
        openSuccessNotification(response?.message);
        props.nextStep?.();
      }
    } catch (error: any) {
      openErrorNotification(error?.message || "Failed to save unit config");
    }
  };

  const isCommercial = String(
    offialState?.propertyPurpose || props?.data?.propertyPurpose || ""
  )
    .toLowerCase()
    .includes("commercial");

  const COMMERCIAL_UNITS = ["Retail Shop", "SCO"];

  const bedroomOptions = (() => {
    const fromMeta = (state?.bedrooms || [])
      .filter((o: any) => {
        const purpose = String(o.propertyPurpose || "").toLowerCase();
        if (isCommercial) {
          return !purpose || purpose.includes("commercial");
        }
        return !purpose || purpose.includes("residential");
      })
      .map((o: any) => String(o.value || "").trim())
      .filter(Boolean);

    if (!isCommercial) {
      return fromMeta.map((value: string) => ({ label: value, value }));
    }

    const commercialMeta = fromMeta.filter(
      (value: string) => !/\bbhk\b/i.test(value) && !/^\d+\s*\+?$/.test(value)
    );
    const merged = Array.from(
      new Set([...commercialMeta, ...COMMERCIAL_UNITS])
    );
    return merged.map((value: string) => ({ label: value, value }));
  })();

  const propertyTypeOptions = (state?.propertyType || []).map((o: any) => ({
    label: o.value,
    value: String(o.value),
  }));

  const currency = state.currency || "INR";

  return (
    <div className="w-full">
      <div className="mb-6 flex items-start gap-3">
        <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#EEF0FF] text-sm font-bold text-[#A02448]">
          ☰
        </span>
        <div>
          <h2 className="text-lg font-bold tracking-tight text-[#111827]">
            Unit Config
          </h2>
          <p className="mt-0.5 text-sm text-[#6B7280]">
            Kindly confirm some facts about the property
          </p>
        </div>
      </div>

      <div className="mb-4 flex items-center justify-between gap-3">
        <h3 className="text-base font-bold text-[#111827]">Price Info</h3>
        <YhButton variant="secondary" size="sm" onClick={handleAddPriceInfo}>
          Add More
        </YhButton>
      </div>

      <div className="space-y-6">
        {(offialState?.priceList || []).map((item: any, index: number) => {
          const activeTab = getActiveTab(index);
          const images =
            item?.floorPlan?.find((fp: any) => fp.type === activeTab)?.images ||
            [];

          return (
            <div
              key={index}
              className="rounded-2xl border border-[#EEF0F5] bg-white p-4 sm:p-5"
            >
              <div className="mb-4 border-b border-[#EEF0F5] pb-3">
                <h4 className="text-sm font-bold text-[#111827]">
                  Unit Config {index + 1}
                </h4>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <YhSelect
                  label={isCommercial ? "Unit Type" : "Bedroom"}
                  placeholder="Select"
                  searchPlaceholder={
                    isCommercial ? "Search unit type..." : "Search bedroom..."
                  }
                  showOptionIcon={false}
                  clearable
                  value={item.bedroom || ""}
                  options={bedroomOptions}
                  onChange={(e) =>
                    handleChange(index, "bedroom", e.target.value)
                  }
                />

                <YhSelect
                  label="Bathroom"
                  placeholder="Select"
                  searchPlaceholder="Search bathroom..."
                  showOptionIcon={false}
                  clearable
                  value={
                    item.bathroom === 0 || item.bathroom
                      ? String(item.bathroom)
                      : ""
                  }
                  options={BATHROOM_OPTIONS}
                  onChange={(e) =>
                    handleChange(index, "bathroom", e.target.value)
                  }
                />

                <YhSelect
                  label="Type of Property"
                  placeholder="Select"
                  searchPlaceholder="Search property type..."
                  showOptionIcon={false}
                  clearable
                  value={item.propertyType || ""}
                  options={propertyTypeOptions}
                  onChange={(e) =>
                    handleChange(index, "propertyType", e.target.value)
                  }
                />

                <div>
                  <YhInput
                    label="Size"
                    placeholder="Enter Size"
                    type="number"
                    value={item.carpetArea ?? ""}
                    endAdornment={
                      <span className="text-xs font-medium text-[#6B7280]">
                        Sq. Ft
                      </span>
                    }
                    onChange={(e) =>
                      handleChange(index, "carpetArea", e.target.value)
                    }
                  />
                  {item.carpetArea && !isNaN(Number(item.carpetArea)) ? (
                    <p className="mt-1.5 text-xs text-[#6B7280]">
                      {(Number(item.carpetArea) / 9).toFixed(2)} Sq. Yd.
                    </p>
                  ) : null}
                </div>

                <div>
                  <YhInput
                    label="Price"
                    placeholder="Enter"
                    type="number"
                    value={item.price ?? ""}
                    startAdornment={
                      <span className="text-xs font-semibold text-[#6B7280]">
                        {currency}
                      </span>
                    }
                    onChange={(e) =>
                      handleChange(index, "price", e.target.value)
                    }
                  />
                  {currency === "INR" && item.price ? (
                    <p className="mt-1.5 text-xs text-[#6B7280]">
                      {(
                        (Number(item.price) * INR_TO_AED_RATE) ||
                        0
                      ).toFixed(2)}{" "}
                      AED
                    </p>
                  ) : null}
                </div>
              </div>

              <div className="mt-5">
                <FieldLabel>Floor Plan (optional)</FieldLabel>
                <div className="mb-3 flex gap-6 border-b border-[#EEF0F5]">
                  {(["2D", "3D"] as FloorPlanType[]).map((tab) => (
                    <button
                      key={tab}
                      type="button"
                      onClick={() =>
                        setFloorPlanTab((prev) => ({ ...prev, [index]: tab }))
                      }
                      className={`relative pb-2 text-sm font-semibold transition ${
                        activeTab === tab
                          ? "text-[#A02448]"
                          : "text-[#9CA3AF] hover:text-[#6B7280]"
                      }`}
                    >
                      {tab}
                      {activeTab === tab ? (
                        <span className="absolute bottom-0 left-0 h-0.5 w-full bg-[#A02448]" />
                      ) : null}
                    </button>
                  ))}
                </div>

                <button
                  type="button"
                  disabled={uploadingIndex === index}
                  onClick={() => fileInputRefs.current[index]?.click()}
                  className="flex w-full flex-col items-center justify-center rounded-2xl border border-dashed border-[#D4B8C0] bg-[#F8F9FF] px-4 py-10 text-center transition hover:border-[#A02448] hover:bg-[#EEF0FF] disabled:opacity-60"
                >
                  <span className="mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-white text-2xl text-[#A02448] shadow-sm">
                    ↑
                  </span>
                  <span className="text-sm font-semibold text-[#374151]">
                    {uploadingIndex === index
                      ? "Uploading..."
                      : "Click or drag file to this area to upload"}
                  </span>
                  <input
                    ref={(el) => {
                      fileInputRefs.current[index] = el;
                    }}
                    type="file"
                    hidden
                    multiple
                    accept="image/*"
                    onChange={(e) => handleFileChange(e, index)}
                  />
                </button>

                {images.length > 0 ? (
                  <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                    {images.map((src: string, imgIndex: number) => (
                      <div
                        key={`${src}-${imgIndex}`}
                        className="group relative overflow-hidden rounded-xl border border-[#EEF0F5]"
                      >
                        <img
                          src={src}
                          alt={`${activeTab} floor plan ${imgIndex + 1}`}
                          className="h-36 w-full object-cover"
                        />
                        <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/40 opacity-0 transition group-hover:opacity-100">
                          <button
                            type="button"
                            onClick={() => window.open(src, "_blank")}
                            className="rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-[#111827]"
                          >
                            View
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              removeImage(index, imgIndex, activeTab)
                            }
                            className="rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-[#DC2626]"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-8 flex items-center justify-between gap-3 border-t border-[#EEF0F5] pt-5">
        <YhButton variant="secondary" onClick={() => props.backStep?.()}>
          ← Back
        </YhButton>
        <YhButton variant="primary" onClick={submitForm}>
          Save & Continue →
        </YhButton>
      </div>
    </div>
  );
};

export default PriceInfo;
